import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useDeleteNotification, useUpdateNotifications } from '@/api/mutations/useNotificationMutations';
import { useCommentableTextAtoms } from '@/comments';
import { DateTypography } from '@/components/DateTypography/DateTypography';
import { Image } from '@/components/ImageAvatar/ImageAvatar';
import { styles } from '@/components/StyledComponents';
import { TEXT_INK, Text } from '@/components/ui/text';
import { STORAGE } from '@/constants';
import { type CommentableModel, CommentNotification } from '@/enums';
import { useWorkspace } from '@/hooks/useWorkspace';
import type { INotification } from '@/types';

interface Props extends INotification {
    handleCloseDrawer: () => void;
    handleSelectComment: (arg: { project_id: number; id: number; type: CommentableModel; comment_id: number; title: string }) => void;
}

export const Notification = (props: Props) => {
    const { t } = useTranslation();
    const {
        id,
        type,
        updated_at,
        read_at,
        handleCloseDrawer,
        data: { project_id, action_url, message, comment, commentable },
    } = props;

    const { register } = useCommentableTextAtoms();
    const navigate = useNavigate();
    const { handleSelectComment } = props;
    const { mutateAsync: updateNotificationsAsync } = useUpdateNotifications();
    const { mutateAsync: deleteNotificationAsync } = useDeleteNotification();
    const switchWorkspace = useWorkspace();

    const handleClick = () => {
        if (!read_at) updateNotificationsAsync({ ids: [id] });
        if (type === CommentNotification.CommentRepliedNotification) {
            if (commentable.text_atom_type_id && commentable.textable_id && commentable.textable_type)
                register({
                    id: commentable.id,
                    text_atom_type_id: commentable.text_atom_type_id,
                    content: commentable.content,
                    project_id,
                    textable_id: commentable.textable_id,
                    textable_type: commentable.textable_type,
                    order: 0,
                    tags: [],
                });
            handleSelectComment({
                project_id,
                id: commentable.id,
                type: commentable.type,
                comment_id: comment.id,
                title: `${
                    commentable.textable_type
                        ? t(`${commentable.textable_type}.${commentable.textable_type}`)
                        : t(`${commentable.type}.${commentable.type}`)
                } ${commentable.name}`,
            });
        } else {
            const url = new URL(action_url);
            if (project_id) switchWorkspace({ projectID: project_id });
            navigate(url);
            handleCloseDrawer();
        }
    };
    const handleDelete = (e: React.SyntheticEvent) => {
        e.stopPropagation();
        deleteNotificationAsync({ id });
    };

    return (
        <>
            {type === CommentNotification.CommentRepliedNotification ? (
                <div className="flex flex-wrap w-full mt-[30px]">
                    <div className="md:grow-0 md:basis-[8.333333%] md:max-w-[8.333333%]">
                        {comment.user.assets.length ? (
                            <Image
                                src={STORAGE + (comment.user.assets[0]?.path ?? '')}
                                alt={comment.user.name}
                                title={comment.user.name}
                                width="32px"
                                height="32px"
                            />
                        ) : (
                            <Image src={' '} alt={comment.user.name} title={comment.user.name} width="32px" height="32px" />
                        )}
                    </div>
                    <Grid
                        item
                        md={10}
                        sx={[
                            styles.notification,
                            {
                                border: theme => (read_at ? 'none' : `1px solid ${theme.palette.secondary.contrastText}`),
                            },
                        ]}
                        onClick={handleClick}
                    >
                        {read_at ? (
                            <CloseIcon onClick={handleDelete} sx={styles.closeIcon} color="info" />
                        ) : (
                            <Box sx={[styles.notificationIndicator]} />
                        )}

                        <Text
                            variant="body1"
                            color={TEXT_INK}
                            children={`${
                                commentable.textable_type
                                    ? t(`${commentable.textable_type}.${commentable.textable_type}`)
                                    : t(`${commentable.type}.${commentable.type}`)
                            } ${commentable.name}`}
                            className="inline-block"
                        />

                        <Text variant="body1" color={TEXT_INK} children={comment.text} />
                        <div className="mt-[20px]">
                            <DateTypography date={comment.created_at} />
                        </div>
                    </Grid>
                </div>
            ) : (
                <div id={id} className="flex flex-wrap w-full mt-[30px]">
                    <Grid
                        item
                        md={11}
                        sx={[
                            styles.notification,
                            {
                                border: theme => (read_at ? 'none' : `1px solid ${theme.palette.secondary.contrastText}`),
                            },
                        ]}
                        onClick={handleClick}
                    >
                        {read_at ? (
                            <CloseIcon onClick={handleDelete} sx={styles.closeIcon} color="info" />
                        ) : (
                            <Box sx={[styles.notificationIndicator]} />
                        )}

                        <Text variant="body1" color={TEXT_INK} children={message} />
                        <div className="mt-[20px]">
                            <DateTypography date={updated_at} />
                        </div>
                    </Grid>
                </div>
            )}
        </>
    );
};
